import { useState } from "react";
import { Search, User, X } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Modal } from "./shared";

export function AwardGoldPanel() {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  // Specific-user award state
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [picked, setPicked] = useState(null);
  const [userAmount, setUserAmount] = useState("");
  const [userBusy, setUserBusy] = useState(false);

  const award = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt < 1) return toast.error("Enter a gold amount of at least 1");
    setBusy(true);
    try {
      const r = await api.post("/admin/award-gold-all", { amount: amt });
      toast.success(`Awarded ${amt} gold to ${r.data.recipients} players`);
      setAmount(""); setConfirm(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const search = async (val) => {
    setQ(val); setPicked(null);
    if (val.trim().length < 2) { setResults(null); return; }
    try {
      const r = await api.get(`/admin/managers?q=${encodeURIComponent(val.trim())}&limit=8`);
      setResults(r.data.managers || []);
    } catch { setResults([]); }
  };

  const giveToUser = async () => {
    const amt = parseInt(userAmount, 10);
    if (!picked) return toast.error("Select a player first");
    if (!amt || amt < 1) return toast.error("Enter a gold amount of at least 1");
    setUserBusy(true);
    try {
      const r = await api.post("/admin/award-gold-user", { user_id: picked.user_id, amount: amt });
      toast.success(`Gave ${amt} gold to ${r.data.name} (now ${r.data.gold.toLocaleString()}g)`, { icon: <GoldCoin size={18} className="text-amber-400" /> });
      setUserAmount(""); setPicked(null); setQ(""); setResults(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setUserBusy(false); }
  };

  return (
    <div data-testid="award-gold-panel" className="mb-6 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <GoldCoin size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Award Gold to All Players</h3>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-zinc-500 flex-1 min-w-[200px]">
          Instantly grants the entered amount of gold to <span className="text-amber-300">every registered player</span> (great for events or apologies). A herald announcement is posted to global chat.
        </p>
        <input data-testid="award-gold-input" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Gold"
          className="w-28 bg-zinc-900 border border-zinc-800 px-3 py-2 text-amber-200 text-sm focus:border-amber-600 outline-none rounded-sm" />
        <button data-testid="award-gold-btn" onClick={() => { const a = parseInt(amount, 10); if (!a || a < 1) return toast.error("Enter a gold amount of at least 1"); setConfirm(true); }} disabled={busy}
          className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50">
          Award to All
        </button>
      </div>

      {/* Specific player */}
      <div className="mt-5 pt-4 border-t border-amber-900/30">
        <div className="flex items-center gap-2 mb-3">
          <User size={15} className="text-amber-400" />
          <h4 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Give Gold to a Specific Player</h4>
        </div>
        <div className="flex flex-wrap items-start gap-3">
          <div className="relative flex-1 min-w-[220px]">
            {picked ? (
              <div data-testid="award-gold-user-picked" className="flex items-center justify-between gap-2 bg-zinc-900 border border-amber-700/50 px-3 py-2 rounded-sm">
                <span className="text-sm text-amber-200 truncate">{picked.name} <span className="text-zinc-500">· {picked.email}</span></span>
                <button data-testid="award-gold-user-clear" onClick={() => { setPicked(null); setQ(""); setResults(null); }} className="text-zinc-500 hover:text-amber-400"><X size={15} /></button>
              </div>
            ) : (
              <>
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input data-testid="award-gold-user-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Search player by name or email…"
                  className="w-full bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none rounded-sm" />
                {results !== null && (
                  <div data-testid="award-gold-user-results" className="absolute z-20 left-0 right-0 mt-1 bg-zinc-950 border border-zinc-800 rounded-sm max-h-56 overflow-y-auto shadow-xl">
                    {results.length === 0 ? <div className="px-3 py-2 text-xs text-zinc-600">No players found</div> :
                      results.map((u) => (
                        <button key={u.user_id} data-testid={`award-gold-user-${u.user_id}`} onClick={() => { setPicked(u); setResults(null); }}
                          className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-amber-900/20 border-b border-zinc-900 last:border-0">
                          {u.name} <span className="text-zinc-500 text-xs">· {u.email}</span>
                        </button>
                      ))}
                  </div>
                )}
              </>
            )}
          </div>
          <input data-testid="award-gold-user-input" type="number" min={1} value={userAmount} onChange={(e) => setUserAmount(e.target.value)} placeholder="Gold"
            className="w-28 bg-zinc-900 border border-zinc-800 px-3 py-2 text-amber-200 text-sm focus:border-amber-600 outline-none rounded-sm" />
          <button data-testid="award-gold-user-btn" onClick={giveToUser} disabled={userBusy || !picked}
            className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed">
            {userBusy ? "Giving…" : "Give Gold"}
          </button>
        </div>
      </div>

      {confirm && (
        <Modal title="Confirm Gold Award" testid="award-gold-confirm" onClose={() => setConfirm(false)}>
          <p className="text-sm text-zinc-300">Give <span className="text-amber-300 font-semibold">{amount} gold</span> to <span className="text-amber-300 font-semibold">every player</span>? This cannot be undone.</p>
          <div className="mt-5 flex gap-2 justify-end">
            <button data-testid="award-gold-cancel" onClick={() => setConfirm(false)} disabled={busy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
            <button data-testid="award-gold-confirm-btn" onClick={award} disabled={busy} className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-50">{busy ? "Awarding…" : "Confirm"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
